import type { AuthMethod, walletMessage } from "@defuse-protocol/internal-utils"
import {
  errors,
  prepareBroadcastRequest,
} from "@defuse-protocol/internal-utils"
import { QuoteRequest } from "@defuse-protocol/one-click-sdk-typescript"
import { wrapPayloadAsWalletMessage } from "@src/components/DefuseSDK/core/messages"
import {
  generateWithdrawIntent,
  getWithdrawQuote as getWithdrawQuoteApi,
  submitWithdrawIntent,
} from "@src/components/DefuseSDK/features/machines/1cs"
import {
  type Quote1csResult,
  quoteFailureReason,
} from "@src/components/DefuseSDK/features/machines/quote1csClient"
import type { IntentDescription } from "@src/components/DefuseSDK/features/machines/swapIntentMachine"
import type {
  BaseTokenInfo,
  SupportedChainName,
  TokenDeployment,
} from "@src/components/DefuseSDK/types/base"
import { assert } from "@src/components/DefuseSDK/utils/assert"
import { AUTH_METHOD_TO_STANDARD } from "@src/components/DefuseSDK/utils/intentStandards"
import { getWithdrawDestinationAsset } from "@src/components/DefuseSDK/utils/oneClickAssetRouting"
import { fetchQuoteOnce } from "@src/components/DefuseSDK/utils/oneClickQuote"
import { adjustDecimals } from "@src/components/DefuseSDK/utils/tokenUtils"
import { verifyQuoteResult } from "@src/components/DefuseSDK/utils/verifyQuoteSignature"
import {
  extractWalletErrorCode,
  type WalletErrorCode,
} from "@src/components/DefuseSDK/utils/walletErrorExtractor"
import { saveQuoteFromResult } from "@src/stores/oneClickQuotesStore"
import { logger } from "@src/utils/logger"
import { assign, fromPromise, log, setup } from "xstate"
import {
  getWithdrawQuoteRecipientDisplayAddress,
  serializeWithdrawQuoteRecipient,
  type WithdrawQuoteRecipient,
} from "../utils/withdrawRecipient"

type Context = {
  input: Input
  userAddress: string
  userChainType: AuthMethod
  quote1csResult: Quote1csResult | null
  walletMessage: walletMessage.WalletMessage | null
  signature: walletMessage.WalletSignatureResult | null
  intentHash: string | null
  error: null | {
    tag: "err"
    value:
      | {
          reason:
            | "ERR_1CS_QUOTE_FAILED"
            | "ERR_QUOTE_SIGNATURE_INVALID"
            | "ERR_NO_DEPOSIT_ADDRESS"
            | "ERR_QUOTE_WORSE_THAN_REVIEWED"
            | "ERR_GENERATE_INTENT_FAILED"
            | "ERR_FAILED_TO_PREPARE_MESSAGE_TO_SIGN"
            | "ERR_USER_DIDNT_SIGN"
            | "ERR_CANNOT_PUBLISH_INTENT"
            | WalletErrorCode
          error: Error | null
        }
      | {
          reason: "ERR_CANNOT_PUBLISH_INTENT"
          server_reason: string
        }
  }
}

type Input = {
  tokenIn: BaseTokenInfo
  tokenOut: BaseTokenInfo
  tokenOutDeployment: TokenDeployment
  destinationNetwork: SupportedChainName | "near_intents"
  swapType: QuoteRequest.swapType
  slippageBasisPoints: number
  defuseUserId: string
  deadline: string
  userAddress: string
  userChainType: AuthMethod
  nearClient: unknown
  amountIn: { amount: bigint; decimals: number }
  recipient: WithdrawQuoteRecipient
  destinationMemo?: string
  virtualChainRecipient?: string
  destinationChainName?: string
  isConfidential?: boolean
  minAmountOut?: bigint
  previousOppositeAmount: { amount: bigint; decimals: number }
  parentRef?: {
    send: (event: {
      type: "EXECUTION_QUOTE_READY"
      params: {
        newAmountIn: { amount: bigint; decimals: number }
        newOppositeAmount: { amount: bigint; decimals: number }
        previousOppositeAmount: { amount: bigint; decimals: number }
      }
    }) => void
  }
}

export type Output =
  | NonNullable<Context["error"]>
  | {
      tag: "ok"
      value: {
        intentHash: string
        depositAddress: string
        intentDescription: IntentDescription
      }
    }

export const withdraw1csMachine = setup({
  types: {
    context: {} as Context,
    input: {} as Input,
    output: {} as Output,
  },
  actions: {
    setError: assign({
      error: (_, error: NonNullable<Context["error"]>["value"]) => ({
        tag: "err" as const,
        value: error,
      }),
    }),
    logError: (_, params: { error: unknown }) => {
      logger.error(params.error)
    },
    set1csQuoteResult: assign({
      quote1csResult: (_, result: NonNullable<Context["quote1csResult"]>) =>
        result,
    }),
    saveOneClickQuote: ({ context }) => {
      saveQuoteFromResult(context.quote1csResult)
    },
    setWalletMessage: assign({
      walletMessage: (_, walletMessage: walletMessage.WalletMessage) =>
        walletMessage,
    }),
    setSignature: assign({
      signature: (_, signature: walletMessage.WalletSignatureResult | null) =>
        signature,
    }),
    setIntentHash: assign({
      intentHash: (_, intentHash: string) => intentHash,
    }),
    notifyQuoteResult: ({ context }) => {
      if (
        context.quote1csResult &&
        "ok" in context.quote1csResult &&
        context.input.parentRef
      ) {
        context.input.parentRef.send({
          type: "EXECUTION_QUOTE_READY",
          params: {
            newAmountIn: {
              amount: BigInt(context.quote1csResult.ok.quote.amountIn),
              decimals: context.input.tokenIn.decimals,
            },
            newOppositeAmount: {
              amount: BigInt(context.quote1csResult.ok.quote.amountOut),
              decimals: context.input.tokenOut.decimals,
            },
            previousOppositeAmount: context.input.previousOppositeAmount,
          },
        })
      }
    },
  },
  actors: {
    fetch1csQuoteActor: fromPromise(
      async ({ input }: { input: Input & { userChainType: AuthMethod } }) => {
        return verifyQuoteResult(
          await fetchQuoteOnce(getWithdrawQuoteApi, {
          dry: false,
          slippageTolerance: Math.round(input.slippageBasisPoints / 100),
          originAsset: input.tokenIn.defuseAssetId,
          destinationAsset:
            input.destinationChainName === "bitcoin"
              ? getWithdrawDestinationAsset(input.tokenOut.defuseAssetId)
              : input.tokenOut.defuseAssetId,
          amount: (input.swapType === QuoteRequest.swapType.EXACT_OUTPUT
            ? adjustDecimals(
                input.amountIn.amount,
                input.amountIn.decimals,
                input.tokenOut.decimals
              )
            : input.amountIn.amount
          ).toString(),
          deadline: input.deadline,
          userAddress: input.userAddress,
          authMethod: input.userChainType,
          swapType: input.swapType,
          ...serializeWithdrawQuoteRecipient(
            input.recipient,
            input.isConfidential
          ),
          ...(input.destinationMemo
            ? { destinationMemo: input.destinationMemo }
            : {}),
          ...(input.virtualChainRecipient
            ? { virtualChainRecipient: input.virtualChainRecipient }
            : {}),
          ...(input.destinationChainName
            ? { destinationChainName: input.destinationChainName }
            : {}),
          ...(input.isConfidential
            ? { isConfidential: input.isConfidential }
            : {}),
          })
        )
      }
    ),
    generateIntentActor: fromPromise(
      async ({
        input,
      }: {
        input: {
          depositAddress: string
          defuseUserId: string
          userChainType: AuthMethod
        }
      }): Promise<walletMessage.WalletMessage> => {
        const standard = AUTH_METHOD_TO_STANDARD[input.userChainType]
        const result = await generateWithdrawIntent({
          depositAddress: input.depositAddress,
          signerId: input.defuseUserId,
          standard,
        })

        if ("err" in result) {
          throw new Error(result.err)
        }

        return wrapPayloadAsWalletMessage(result.ok.intent)
      }
    ),
    signMessage: fromPromise(
      async (_: {
        input: walletMessage.WalletMessage
      }): Promise<walletMessage.WalletSignatureResult | null> => {
        throw new Error("signMessage actor must be provided by the parent")
      }
    ),
    submitIntentActor: fromPromise(
      async ({
        input,
      }: {
        input: {
          signatureData: walletMessage.WalletSignatureResult
          userInfo: { userAddress: string; userChainType: AuthMethod }
        }
      }) => {
        const signedIntent = prepareBroadcastRequest.prepareSwapSignedData(
          input.signatureData,
          input.userInfo
        )
        const result = await submitWithdrawIntent({ signedIntent })
        if ("err" in result) {
          return { tag: "err" as const, value: { reason: result.err } }
        }
        return { tag: "ok" as const, value: result.ok.intentHash }
      }
    ),
  },
  guards: {
    isTrue: (_, params: boolean) => params,
    isOk: (_, params: { tag: "ok" } | { tag: "err" }) => params.tag === "ok",
    isWorseThanPrevious: ({ context }) => {
      if (
        context.quote1csResult == null ||
        !("ok" in context.quote1csResult) ||
        context.quote1csResult.ok.quote.amountOut == null
      ) {
        return false
      }
      return (
        BigInt(context.quote1csResult.ok.quote.amountOut) <
        context.input.previousOppositeAmount.amount
      )
    },
    isQuoteSuccess: ({ context }) => {
      if (context.quote1csResult == null || "err" in context.quote1csResult) {
        return false
      }

      if (context.quote1csResult.ok.quote.depositAddress == null) {
        return false
      }

      const quoteAmountIn = parseQuoteAmount(
        context.quote1csResult.ok.quote.amountIn
      )
      const quoteAmountOut = parseQuoteAmount(
        context.quote1csResult.ok.quote.amountOut
      )

      // In EXACT_INPUT mode, verify quote amountIn matches the requested input.
      // In EXACT_OUTPUT mode, verify quote amountOut matches the requested output.
      if (context.input.swapType === QuoteRequest.swapType.EXACT_INPUT) {
        if (
          quoteAmountIn === null ||
          quoteAmountIn !== context.input.amountIn.amount
        ) {
          return false
        }
      } else if (
        context.input.swapType === QuoteRequest.swapType.EXACT_OUTPUT
      ) {
        if (
          quoteAmountOut === null ||
          quoteAmountOut <
            adjustDecimals(
              context.input.amountIn.amount,
              context.input.amountIn.decimals,
              context.input.tokenOut.decimals
            )
        ) {
          return false
        }
      }

      if (context.input.minAmountOut != null) {
        if (
          quoteAmountOut === null ||
          quoteAmountOut < context.input.minAmountOut
        ) {
          return false
        }
      }

      return true
    },
  },
}).createMachine({
  id: "withdraw-intent-1cs",

  context: ({ input }) => ({
    input,
    userAddress: input.userAddress,
    userChainType: input.userChainType,
    quote1csResult: null,
    walletMessage: null,
    signature: null,
    intentHash: null,
    error: null,
  }),

  initial: "Fetching1csQuote",

  output: ({ context }): Output => {
    if (context.intentHash != null) {
      assert(
        context.quote1csResult != null &&
          "ok" in context.quote1csResult &&
          context.quote1csResult.ok.quote.depositAddress != null,
        "Deposit address must be set when intent hash is available"
      )

      return {
        tag: "ok",
        value: {
          intentHash: context.intentHash,
          depositAddress: context.quote1csResult.ok.quote.depositAddress,
          intentDescription: {
            type: "withdraw",
            tokenOut: context.input.tokenOut,
            tokenOutDeployment: context.input.tokenOutDeployment,
            destinationNetwork: context.input.destinationNetwork,
            recipient: getWithdrawQuoteRecipientDisplayAddress(
              context.input.recipient
            ),
            depositAddress: context.quote1csResult.ok.quote.depositAddress,
            totalAmountIn: {
              amount: BigInt(context.quote1csResult.ok.quote.amountIn ?? "0"),
              decimals: context.input.tokenIn.decimals,
            },
            totalAmountOut: {
              amount: BigInt(context.quote1csResult.ok.quote.amountOut ?? "0"),
              decimals: context.input.tokenOut.decimals,
            },
          },
        },
      }
    }

    if (context.error != null) {
      return context.error
    }

    throw new Error("Unexpected output state")
  },

  states: {
    Fetching1csQuote: {
      invoke: {
        src: "fetch1csQuoteActor",
        input: ({ context }) => context.input,
        onDone: {
          target: "ValidatingQuote",
          actions: [
            {
              type: "set1csQuoteResult",
              params: ({ event }) => event.output,
            },
          ],
        },
        onError: {
          target: "Error",
          actions: [
            {
              type: "logError",
              params: ({ event }) => event,
            },
            {
              type: "setError",
              params: ({ event }) => {
                return {
                  reason: "ERR_1CS_QUOTE_FAILED",
                  error:
                    event.error instanceof Error
                      ? event.error
                      : new Error(String(event.error)),
                }
              },
            },
          ],
        },
      },
    },

    ValidatingQuote: {
      always: [
        {
          target: "AwaitingUserConfirmation",
          guard: {
            type: "isWorseThanPrevious",
          },
        },
        {
          target: "AwaitingUserConfirmation",
          guard: {
            type: "isQuoteSuccess",
          },
        },
        {
          target: "Error",
          actions: {
            type: "setError",
            params: ({ context }) => {
              if (!context.quote1csResult || "err" in context.quote1csResult) {
                const err =
                  context.quote1csResult && "err" in context.quote1csResult
                    ? context.quote1csResult.err
                    : "Unknown quote error"
                return {
                  reason: quoteFailureReason(err),
                  error: new Error(err),
                }
              }

              if (context.quote1csResult.ok.quote.depositAddress == null) {
                return {
                  reason: "ERR_NO_DEPOSIT_ADDRESS",
                  error: new Error(
                    "1CS quote succeeded but no deposit address provided"
                  ),
                }
              }

              const quoteAmountIn = parseQuoteAmount(
                context.quote1csResult.ok.quote.amountIn
              )

              if (
                quoteAmountIn === null ||
                quoteAmountIn !== context.input.amountIn.amount
              ) {
                return {
                  reason: "ERR_1CS_QUOTE_FAILED",
                  error: new Error(
                    "Quoted amountIn does not match requested withdrawal amount"
                  ),
                }
              }

              if (context.input.minAmountOut != null) {
                const quoteAmountOut = parseQuoteAmount(
                  context.quote1csResult.ok.quote.amountOut
                )
                if (
                  quoteAmountOut === null ||
                  quoteAmountOut < context.input.minAmountOut
                ) {
                  return {
                    reason: "ERR_QUOTE_WORSE_THAN_REVIEWED",
                    error: new Error(
                      `Executed amountOut ${quoteAmountOut} < reviewed ${context.input.minAmountOut}`
                    ),
                  }
                }
              }

              return {
                reason: "ERR_1CS_QUOTE_FAILED",
                error: new Error("1CS quote validation failed"),
              }
            },
          },
        },
      ],
    },

    AwaitingUserConfirmation: {
      entry: "notifyQuoteResult",
      on: {
        CONFIRMED: {
          target: "GeneratingIntent",
        },
        CANCELLED: {
          target: "Error",
          actions: {
            type: "setError",
            params: {
              reason: "ERR_USER_DIDNT_SIGN",
              error: null,
            },
          },
        },
      },
    },

    GeneratingIntent: {
      invoke: {
        src: "generateIntentActor",
        input: ({ context }) => {
          assert(
            context.quote1csResult != null && "ok" in context.quote1csResult
          )
          assert(context.quote1csResult.ok.quote.depositAddress != null)
          return {
            depositAddress: context.quote1csResult.ok.quote.depositAddress,
            defuseUserId: context.input.defuseUserId,
            userChainType: context.input.userChainType,
          }
        },
        onDone: {
          target: "Signing",
          actions: {
            type: "setWalletMessage",
            params: ({ event }) => event.output,
          },
        },
        onError: {
          target: "Error",
          actions: [
            {
              type: "logError",
              params: ({ event }) => event,
            },
            {
              type: "setError",
              params: ({ event }) => ({
                reason: "ERR_GENERATE_INTENT_FAILED",
                error:
                  event.error instanceof Error
                    ? event.error
                    : new Error(String(event.error)),
              }),
            },
          ],
        },
      },
    },

    Signing: {
      invoke: {
        id: "signMessage",
        src: "signMessage",
        input: ({ context }) => {
          assert(context.walletMessage != null, "Wallet message is not set")
          return context.walletMessage
        },
        onDone: {
          target: "SubmittingIntent",
          actions: [
            {
              type: "setSignature",
              params: ({ event }) => event.output,
            },
            "saveOneClickQuote",
          ],
        },
        onError: {
          target: "Error",
          actions: [
            {
              type: "logError",
              params: ({ event }) => event,
            },
            {
              type: "setError",
              params: ({ event }) => ({
                reason: extractWalletErrorCode(
                  event.error,
                  "ERR_USER_DIDNT_SIGN"
                ),
                error: errors.toError(event.error),
              }),
            },
          ],
        },
      },
    },

    SubmittingIntent: {
      invoke: {
        src: "submitIntentActor",
        input: ({ context }) => {
          assert(context.signature != null, "Signature is not set")

          return {
            signatureData: context.signature,
            userInfo: {
              userAddress: context.userAddress,
              userChainType: context.userChainType,
            },
          }
        },
        onDone: [
          {
            target: "Completed",
            guard: {
              type: "isOk",
              params: ({ event }) => event.output,
            },
            actions: [
              log("Withdraw intent submitted"),
              {
                type: "setIntentHash",
                params: ({ event }) => {
                  assert(event.output.tag === "ok")
                  return event.output.value
                },
              },
            ],
          },
          {
            target: "Error",
            actions: [
              {
                type: "logError",
                params: ({ event }) => {
                  assert(event.output.tag === "err")
                  return {
                    error: new Error(
                      `Failed to submit withdraw intent: ${event.output.value.reason}`
                    ),
                  }
                },
              },
              {
                type: "setError",
                params: ({ event }) => {
                  assert(event.output.tag === "err")
                  return {
                    reason: "ERR_CANNOT_PUBLISH_INTENT",
                    server_reason: event.output.value.reason,
                  }
                },
              },
            ],
          },
        ],
        onError: {
          target: "Error",
          actions: [
            {
              type: "logError",
              params: ({ event }) => event,
            },
            {
              type: "setError",
              params: ({ event }) => ({
                reason: "ERR_CANNOT_PUBLISH_INTENT",
                error: errors.toError(event.error),
              }),
            },
          ],
        },
      },
    },

    Completed: {
      type: "final",
    },

    Error: {
      type: "final",
    },
  },
})

function parseQuoteAmount(amountIn: string): bigint | null {
  try {
    return BigInt(amountIn)
  } catch (error) {
    logger.error("Failed to parse quote amountIn as BigInt", {
      amountIn,
      error,
    })
    return null
  }
}
